import { HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from '../../environments/environment';
import { LicensePlateService } from '../services';

const LICENSE_PLATE_HEADER = 'x-license-plate';
// Defense in depth: refuse anything that could escape the header value
// (CR/LF, control chars, unreasonably long values) — same shape as
// LicensePlateService.setCurrentLicensePlate.
const LICENSE_PLATE_VALIDATION = /^[A-Z0-9]{1,8}$/;

function isApiUrl(url: string): boolean {
  return url.startsWith(`${environment.apiUrl}/`);
}

/**
 * Adds the `x-license-plate` header to every API request when a license plate is
 * set on the LicensePlateService. The value comes from the service signal (set by
 * the page in the booking flow), never from the URL — so it can't be spoofed via
 * `window.location`. Scoped to the trusted API origin (`/api/`) so the plate (PII)
 * is never attached to a cross-origin request.
 */
export const licensePlateInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  if (!isApiUrl(req.url)) {
    return next(req);
  }

  const plate: string | null = inject(LicensePlateService).currentLicensePlate();

  if (!plate || !LICENSE_PLATE_VALIDATION.test(plate)) {
    return next(req);
  }

  return next(
    req.clone({
      setHeaders: { [LICENSE_PLATE_HEADER]: plate },
    }),
  );
};
